// Mercapoli - Customer Profile Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, Save, Loader2, LogOut, Moon, Sun, Coins } from 'lucide-react';
import CustomerLayout from '../../layouts/CustomerLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { diggii as diggiiApi } from '../../lib/api';
import { toast } from 'sonner';
import DiggiiBalance from '../../components/DiggiiBalance';
import DiggiiHistory from '../../components/DiggiiHistory';

const CustomerProfile = () => {
  const navigate = useNavigate();
  const { user, updateProfile, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [diggiiData, setDiggiiData] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [formData, setFormData] = useState({
    nombre: user?.nombre || '',
    telefono: user?.telefono || '',
    direccion: user?.direccion || '',
    ciudad: user?.ciudad || ''
  });

  useEffect(() => {
    loadDiggiiBalance();
  }, []);

  const loadDiggiiBalance = async () => {
    try {
      const data = await diggiiApi.getBalance();
      setDiggiiData(data);
    } catch (error) {
      console.error('Error cargando DIGGII:', error);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await updateProfile(formData);
      toast.success('Perfil actualizado');
    } catch (error) {
      toast.error('Error al actualizar el perfil');
    }
    
    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <CustomerLayout title="Mi perfil" showSearch={false}>
      <div className="container-app pb-6">
        {/* Avatar section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center mb-6"
        >
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-spicy-coral to-[#FF8E53] flex items-center justify-center mb-4">
            <span className="text-white text-3xl font-bold">
              {user?.nombre?.charAt(0).toUpperCase()}
            </span>
          </div>
          <h2 className="font-heading text-xl font-bold">{user?.nombre}</h2>
          <p className="text-muted-foreground">{user?.email}</p>
        </motion.div>

        {/* DIGGII Balance Card */}
        {diggiiData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mb-6"
          >
            <DiggiiBalance
              balance={diggiiData.balance}
              valorLocal={diggiiData.valor_en_local}
              simbolo={diggiiData.simbolo}
              progresoSiguiente={diggiiData.progreso_siguiente_diggii}
              faltaParaSiguiente={diggiiData.falta_para_siguiente}
              ratioAcumulacion={diggiiData.ratio_acumulacion}
              onClick={() => setShowHistory(true)}
            />
          </motion.div>
        )}

        {/* Profile form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          <div className="bg-card rounded-xl border border-border p-4 space-y-4">
            <h3 className="font-heading font-bold">Información personal</h3>
            
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre completo</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className="pl-10"
                  placeholder="Tu nombre"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  value={user?.email}
                  className="pl-10 bg-muted"
                  disabled
                />
              </div>
              <p className="text-xs text-muted-foreground">
                El correo no puede ser modificado
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefono">Teléfono</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  className="pl-10"
                  placeholder="+52 55 1234 5678"
                />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-4 space-y-4">
            <h3 className="font-heading font-bold">Dirección de entrega</h3>
            
            <div className="space-y-2">
              <Label htmlFor="direccion">Dirección</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="direccion"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  className="pl-10"
                  placeholder="Calle, número, colonia..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ciudad">Ciudad</Label>
              <Input
                id="ciudad"
                name="ciudad"
                value={formData.ciudad}
                onChange={handleChange}
                placeholder="Ciudad de México"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-spicy-coral hover:bg-spicy-coral/90"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Guardar cambios
              </>
            )}
          </Button>
        </motion.form>

        {/* Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-6 bg-card rounded-xl border border-border p-4"
        >
          <h3 className="font-heading font-bold mb-4">Preferencias</h3>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isDark ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              <div>
                <p className="font-medium">Modo oscuro</p>
                <p className="text-sm text-muted-foreground">
                  Cambiar apariencia de la aplicación
                </p>
              </div>
            </div>
            <Switch
              checked={isDark}
              onCheckedChange={toggleTheme}
            />
          </div>
        </motion.div>

        {/* Logout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 space-y-3"
        >
          <Button
            variant="outline"
            className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar sesión
          </Button>
        </motion.div>

        {/* Legal Links & Account Management (Required for App Store) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mt-6 pt-4 border-t border-border"
        >
          <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
            <div className="flex gap-4">
              <a href="/privacidad" className="hover:text-primary">Política de Privacidad</a>
              <span>|</span>
              <a href="/terminos" className="hover:text-primary">Términos</a>
            </div>
            <a 
              href="/eliminar-cuenta" 
              className="text-destructive/70 hover:text-destructive text-xs"
            >
              Eliminar mi cuenta
            </a>
          </div>
        </motion.div>

        {/* DIGGII History Modal */}
        <DiggiiHistory 
          isOpen={showHistory} 
          onClose={() => setShowHistory(false)} 
        />
      </div>
    </CustomerLayout>
  );
};

export default CustomerProfile;
